import React, { useContext } from "react";
import { useAuth0 } from "./AuthProvider";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  CreateAPIParams,
  Place,
  SendRequestParams,
  TokenizedAPIParams,
  WebServerContextType,
} from "../types";

const baseUrlSec =
  process.env.REACT_APP_ENV !== "dev" ? "https://" : "http://";
const baseURL = process.env.REACT_APP_BACKEND_ENDPOINT || "";
// const ACAO =
//   process.env.REACT_APP_ENV !== "dev"
//     ? process.env.REACT_APP_ENV === "staging"
//       ? "https://mos-id-backend-staging.vectech.io"
//       : "https://mos-id-backend.vectech.io"
//     : "*";

const apiConfig = {
  mainAPI: {
    baseURL: baseUrlSec + baseURL,
  },
};

const WebServerContext = React.createContext<WebServerContextType | undefined>(
  undefined
);

const WebServerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getAccessTokenSilently, getAccessTokenWithPopup, isLoading, user, logout } =
    useAuth0();

  const getToken = async (ignoreCache = false) => {
    try {
      // return await getAccessTokenSilently();
      return await getAccessTokenSilently(ignoreCache ? { cacheMode: "off" } : { cacheMode: "on" });
    } catch (e: Error | any) {
      console.error("Failed to get token silently: " + e);
      const message = `${e}`;
      if (message && message.toLowerCase().includes("login required")) {
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
      else {
        try {
          return await getAccessTokenWithPopup();
        } catch (e) {
            console.error("Failed to get token with popup: " + e);
            if (message && message.toLowerCase().includes("login required")) {
            logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            });
          }
        }
      }

    }
  };

  const createApi = (params: CreateAPIParams) =>
    axios.create({
      baseURL: params.baseURL,
      headers: {
        Authorization: `Bearer ${params.token}`,
        "Content-Type": "application/json",
        // "Access-Control-Allow-Origin": ACAO,
      },
    });

  const createTokenizedAPI = async (params: TokenizedAPIParams) => {
    const token = (await getToken()) || null;
    if (!token) {
      throw new Error("Failed to get token");
    }
    return createApi({
      baseURL:
        params?.devPort && process.env.REACT_APP_ENV === "dev"
          ? String(apiConfig.mainAPI.baseURL).slice(0, -4) + params.devPort
          : apiConfig.mainAPI.baseURL,
      token,
    });
  };

  const getGPSLocationFromAddress = async (address: string) => {
    const resp = await axios.get(
      `https://nominatim.openstreetmap.org/search?format=json&q='+${address}`,
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
        },
      }
    );
    if (resp?.data?.length > 0) {
      const tmp = resp.data.map((cur: Place) => {
        return {
          display_name: cur.display_name,
          lat: cur.lat,
          lon: cur.lon,
        };
      });

      return tmp.filter(
        (cur: Place, index: number, self: Place[]) =>
          index === self.findIndex((u) => u.display_name === cur.display_name)
      );
    }
    return [];
  };

  const getAddressFromGPSLocation = async (lat: string, lng: string) => {
    try {
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );
      const cur = resp.data;
      return {
        display_name: cur?.display_name || "",
        lat: cur?.lat || "",
        lon: cur?.lon || "",
      };
    } catch (err) {
      console.log(err);
    }
  };

   const sendRequest =
    (method: string) =>
    async <T, D>(
      sendParams: SendRequestParams<D>
    ): Promise<AxiosResponse<T, D>> => {
      const { devPort, ...rest } = sendParams;
      const resp: AxiosRequestConfig = {
        ...rest,
        method,
      };
      const api = await createTokenizedAPI({ devPort });
      const response = await api.request(resp);
      return response;
    };

  return (
    <WebServerContext.Provider
      value={{
        sendRequest,
        getToken,
        isLoading,
        user,
        getGPSLocationFromAddress,
        getAddressFromGPSLocation,
      }}
    >
      {children}
    </WebServerContext.Provider>
  );
};

const useWebServer = () => {
  const context = useContext(WebServerContext);
  if (!context) {
    throw new Error("UseWebServer must be used within a WebServerProvider");
  }
  return context;
};

export default WebServerProvider;
export { useWebServer };
